/**
 * Where a line is chosen — and remembered, so it is not chosen again soon.
 *
 * The shared engine owns the rules (buckets, notable triggers, the repeat
 * window); this file owns the app's side of them: the register on the
 * profile, the catalogue cascade, the cast merge, and the memory of what was
 * already said. Memory used to live in localStorage under one shared key,
 * which meant it bled between the two accounts sharing a phone and was lost
 * outright in iOS standalone WebKit's aggressive private-storage eviction.
 * It now lives in Dexie meta, one row per account (`sass.memory.<userId>`),
 * mirrored in an in-memory cache so every read here stays synchronous:
 * callers up and down the app call `sassLine` from inside event handlers and
 * cannot await a database round-trip to show a toast.
 */

import {
  bucketFor,
  NEGLECT_THRESHOLD_DAYS,
  neglectTier,
  type PoolPick,
  pickIndex,
  pruneHistory,
  REPEAT_WINDOW_MS,
  type SassContext,
  type SassEvent,
} from '@gramas/shared'
import { getMeta, setMeta } from '../db/dexie.js'
import { affectionNow } from '../glitime/interaction.js'
import {
  __hydrateLinesForTests,
  __resetLinesForTests,
  type CachedLine,
  personalFor,
  personalLogReactions,
  resolvePersonalPin,
  takePersonalLine,
} from '../glitime/lines.js'
import { markMoment } from '../glitime/moments.js'
import { currentProfile } from '../screens/useProfile.js'
import { playCue } from '../sound/engine.js'
import {
  AFFECTION_COOLED,
  AFFECTION_WARMED,
  CAMEO,
  CATALOG,
  EASTER_EGGS,
  EIMANTAS,
  EXCLUSIVE,
  HERLIFE,
  KATE,
  type Line,
  MOOD_OVERLAYS,
  NEGLECTED,
  type Pool,
} from './catalog/index.js'
import { DRY_CATALOG } from './catalog-dry/index.js'

type Memory = Record<string, PoolPick[]>
// A pin is a catalogue index (existing data, unchanged), or `p:<lineId>` for
// a personal-line pin — a string rather than a second index space, because a
// personal line has no position in any pool to be an index into.
type DailyPicks = Record<string, number | string>
type Register = 'sassy' | 'dry'

/** Whoever hasn't signed in yet — the login screen speaks before any account exists. */
const ANON_OWNER = 'anon'

function ownerKey(): string {
  return currentProfile()?.id ?? ANON_OWNER
}

function memoryMetaKey(owner: string): string {
  return `sass.memory.${owner}`
}
function dailyMetaKey(owner: string): string {
  return `sass.daily.${owner}`
}

// One cache entry per account, so switching between the two people sharing a
// phone reads and writes two independent rotations rather than one shared
// one. Reads are synchronous against this cache; a write updates it
// immediately and persists to Dexie in the background — the persistence
// racing ahead or failing outright costs a joke heard slightly early, never
// correctness within the session.
const memoryCache = new Map<string, Memory>()
const dailyCache = new Map<string, DailyPicks>()
const hydrationPromises = new Map<string, Promise<void>>()

function hydrate(owner: string): Promise<void> {
  let promise = hydrationPromises.get(owner)
  if (promise == null) {
    promise = Promise.all([
      getMeta<Memory>(memoryMetaKey(owner), {}),
      getMeta<DailyPicks>(dailyMetaKey(owner), {}),
    ]).then(([memory, daily]) => {
      // A write already landed while this was in flight — that write is the
      // truth now, adopting the read underneath it would throw a live pick away.
      if (!memoryCache.has(owner)) memoryCache.set(owner, memory)
      if (!dailyCache.has(owner)) dailyCache.set(owner, daily)
    })
    hydrationPromises.set(owner, promise)
  }
  return promise
}

function readMemory(owner: string): Memory {
  void hydrate(owner)
  return memoryCache.get(owner) ?? {}
}
function writeMemory(owner: string, memory: Memory): void {
  memoryCache.set(owner, memory)
  void setMeta(memoryMetaKey(owner), memory)
}
function readDaily(owner: string): DailyPicks {
  void hydrate(owner)
  return dailyCache.get(owner) ?? {}
}
function writeDaily(owner: string, daily: DailyPicks): void {
  dailyCache.set(owner, daily)
  void setMeta(dailyMetaKey(owner), daily)
}

/** Test-only: wait for an account's Dexie-backed memory to finish loading. */
export function __hydrateMemoryForTests(owner: string): Promise<void> {
  return hydrate(owner)
}
/** Test-only: drop the in-memory cache so the next read starts cold. */
export function __resetMemoryCacheForTests(): void {
  memoryCache.clear()
  dailyCache.clear()
  hydrationPromises.clear()
}

/** Test-only: wait for an account's Dexie-backed personal-line cache to load. */
export function __hydratePersonalLinesForTests(owner: string): Promise<void> {
  return __hydrateLinesForTests(owner)
}
/** Test-only: drop the personal-line cache and its shown-at history. */
export function __resetPersonalLinesCacheForTests(): void {
  __resetLinesForTests()
}

/**
 * Secret finds already noted this session, so a screen that re-renders on the
 * one minute of the year an easter egg is live does not queue a write per
 * render. `markMoment` is idempotent anyway; this keeps it from being asked.
 */
const notedFinds = new Set<string>()

function noteSecretFound(find: string): void {
  if (notedFinds.has(find)) return
  notedFinds.add(find)
  playCue('secret')
  void markMoment('secret', find).catch(() => {
    // A find that failed to record is a line that was still shown. Retrying
    // it would mean the pool lookup could throw, which is a worse trade.
    notedFinds.delete(find)
  })
}

/** Test-only: forget which secret finds have already been noted this session. */
export function __resetSecretFindsForTests(): void {
  notedFinds.clear()
}

/**
 * Which of the two hand-written voices is on. "Dalykiška" used to mean
 * silence; it now means the same narrator with the volume down, so both
 * values pick a catalogue rather than one of them picking none.
 */
export function register(): Register {
  return currentProfile()?.tone === 'neutral' ? 'dry' : 'sassy'
}

/** What every template may know without the call site repeating itself. */
function fullContext(ctx: Partial<SassContext>): SassContext {
  const profile = currentProfile()
  return {
    hour: new Date().getHours(),
    name: profile?.displayName ?? null,
    // Lines about Saulius (and material addressed to Eimantas himself) are
    // for the account that is not his own — never for the admin's account.
    cameoOk: profile != null && !profile.isAdmin,
    // The interaction layer's shade on the day — warmed by petting, cooled by
    // a rude chat exit. Synchronous by the same cache trick as sass memory.
    affection: affectionNow() ?? undefined,
    ...ctx,
  }
}

/**
 * The pool for an event in this context: most specific key that exists wins,
 * food-class and phase pools are sparse overrides. Four layers sit on top of
 * that cascade, checked in this order:
 *
 * 1. **Exclusive** — a calendar date matches (Katė's birthday, Vėlinės).
 *    Replaces the pool outright; an exclusive that has to compete for the
 *    draw is not exclusive.
 * 2. **Neglected** — the diary has been silent for days. Replaces the pool
 *    with the register for how long the silence has run, keyed by event
 *    rather than by bucket: after a week away, the hour is not the joke.
 * 3. **Mood** — `loyal`, `proud`, `gloomy` or `festive`. Replaces the pool
 *    with that mood's counterpart where one exists (see `MOOD_OVERLAYS`).
 *    Sparse by design: a mood colours the surfaces where it has something to
 *    say and leaves the rest of the catalogue alone.
 * 4. **Her own day** — the seed says she has something of her own to report
 *    (`herlife.ts`). Below the moods on purpose: whatever is going on with the
 *    oven does not outrank a fortnight of silence or a genuinely bad week.
 * 5. **Cast** — the sassy register only: Katė rides along unconditionally,
 *    the Saulius cameo and the Eimantas-specific material only where the
 *    account allows cameos at all.
 *
 * Neglect is checked before loyalty only for readability; `computeMood`
 * returns one word, so the two can never both be true.
 *
 * The dry register short-circuits before any of this: PERSONA.md calls it
 * "the same her, muzzled," and a muzzled voice doesn't introduce characters,
 * celebrate a birthday, or turn dry-loyal — it is already dry.
 */
export function resolvePool(
  event: SassEvent,
  ctx: SassContext,
  now: Date = new Date(),
): { key: string; pool: Pool } | null {
  const bucket = bucketFor(event, ctx)
  // Food class ahead of phase: what was eaten is the more specific fact, and
  // the two overrides are both sparse enough that they rarely both exist.
  const keys = [
    ...(ctx.foodClass != null ? [`${event}.${bucket}.${ctx.foodClass}`] : []),
    ...(ctx.phase ? [`${event}.${bucket}.${ctx.phase}`] : []),
    `${event}.${bucket}`,
    `${event}.base`,
  ]
  const dry = register() === 'dry'
  const source = dry ? DRY_CATALOG : CATALOG
  for (const key of keys) {
    const pool = source[key]
    if (pool == null || pool.length === 0) continue
    if (dry) return { key, pool }

    const exclusive = [...(EXCLUSIVE[key] ?? []), ...(EASTER_EGGS[key] ?? [])].filter((entry) =>
      entry.when(now),
    )
    if (exclusive.length > 0) {
      // The ones worth finding leave a trace in her file. Recorded here rather
      // than at the call site because this is the only place that knows an
      // exclusive matched at all — and fire-and-forget, because a line must
      // never wait on a database write to be shown.
      for (const entry of exclusive) if (entry.find != null) noteSecretFound(entry.find)
      return { key, pool: exclusive.map((entry) => entry.line) }
    }

    if (ctx.mood === 'neglected') {
      const tierKey = `${event}.${neglectTier(ctx.daysSinceLastLog ?? NEGLECT_THRESHOLD_DAYS)}`
      const neglected = NEGLECTED[tierKey]
      if (neglected != null && neglected.length > 0) return { key: tierKey, pool: neglected }
    }

    if (ctx.mood != null) {
      const overlay = MOOD_OVERLAYS[ctx.mood]?.[key]
      if (overlay != null && overlay.length > 0) return { key, pool: overlay }
    }

    // The cooled shade: below every real mood, above her own day. After a
    // rude chat exit she is short with this account until tomorrow — no
    // household chatter, no cast, no bits. Keys with no cooled counterpart
    // fall through to the base pool *alone*, which is most of the coolness.
    if (ctx.affection === 'cooled') {
      const cooled = AFFECTION_COOLED[key]
      if (cooled != null && cooled.length > 0) return { key: `${key}#cooled`, pool: cooled }
      return { key, pool }
    }

    if (ctx.herLife != null) {
      const herKey = `${event}.${ctx.herLife}`
      const hers = HERLIFE[herKey]
      if (hers != null && hers.length > 0) return { key: herKey, pool: hers }
    }

    const cast: Line[] = [...(KATE[key] ?? [])]
    if (ctx.cameoOk === true) cast.push(...(CAMEO[key] ?? []), ...(EIMANTAS[key] ?? []))
    // The warmed shade rides along the way the cast does: a few extra lines
    // in the draw on a day that has had enough petting, never a takeover.
    if (ctx.affection === 'warmed') cast.push(...(AFFECTION_WARMED[key] ?? []))
    return { key, pool: cast.length > 0 ? [...pool, ...cast] : pool }
  }
  return null
}

/** How often a personal callback wins the draw when one is available — a
 * treat layered on the catalogue, never a takeover of it. */
const PERSONAL_RATE = 1 / 3

/**
 * A pre-generated personal line for this event, drawn AFTER the catalogue's
 * own pick so `resolvePool`'s array-indexed repeat-history always runs
 * unchanged whether or not a personal line ends up winning — see
 * `glitime/lines.ts`'s header for why the two draws must stay entirely
 * separate. Null whenever the register, mood or affection say this is not
 * her moment for a callback, or there was simply nothing to draw.
 *
 * `greeting`/`presence_remark` only look at the coin once a candidate exists
 * — an account with no personal lines yet must draw `rng()` exactly as many
 * times as it always has. `log_success` always uses a match when one exists:
 * a concrete food-name hit is rare enough on its own not to need throttling.
 */
function personalOverride(
  event: SassEvent,
  ctx: SassContext,
  rng: () => number,
): CachedLine | null {
  if (register() !== 'sassy') return null
  if (ctx.mood === 'neglected' || ctx.mood === 'loyal') return null
  if (ctx.affection === 'cooled') return null

  switch (event) {
    case 'greeting':
    case 'presence_remark': {
      const candidates = personalFor(event)
      if (candidates.length === 0 || rng() >= PERSONAL_RATE) return null
      return takePersonalLine(candidates, REPEAT_WINDOW_MS, rng)
    }
    case 'log_success':
      return takePersonalLine(
        personalLogReactions(ctx.itemName, ctx.foodClass ?? undefined),
        REPEAT_WINDOW_MS,
        rng,
      )
    default:
      return null
  }
}

/**
 * One line, or silence: null when no pool matches this key in the active
 * register. The pick is recorded, which is what makes the next pick a
 * different joke.
 */
export function sassLine(
  event: SassEvent,
  ctx: Partial<SassContext> = {},
  rng: () => number = Math.random,
  now: number = Date.now(),
): string | null {
  const full = fullContext(ctx)
  const resolved = resolvePool(event, full, new Date(now))
  if (resolved == null) return null

  const owner = ownerKey()
  const memory = readMemory(owner)
  const history = pruneHistory(memory[resolved.key] ?? [], now)
  const index = pickIndex(resolved.pool.length, history, now, rng)
  if (index < 0) return null

  writeMemory(owner, {
    ...memory,
    [resolved.key]: [...history.filter((pick) => pick.i !== index), { i: index, t: now }],
  })

  const personal = personalOverride(event, full, rng)
  if (personal != null) return personal.text
  return resolved.pool[index]!(full)
}

/**
 * A line that holds still for a day: the greeting, the ring commentary, the
 * card titles. The *choice* is pinned per date (and per bucket, so an over
 * budget day switching from a stumble to a statement re-picks), while the
 * template re-renders with live numbers — a frozen joke may not carry a stale
 * figure. Re-renders cost nothing; only the first call of a day draws.
 */
export function dailyLine(
  event: SassEvent,
  dateKey: string,
  ctx: Partial<SassContext> = {},
  rng: () => number = Math.random,
  now: number = Date.now(),
): string | null {
  const full = fullContext(ctx)
  const resolved = resolvePool(event, full, new Date(now))
  if (resolved == null) return null

  const owner = ownerKey()
  const dayKey = `${resolved.key}#${dateKey}`
  const daily = readDaily(owner)
  const pinned = daily[dayKey]
  if (typeof pinned === 'string') {
    if (pinned.startsWith('p:')) {
      const personalPin = resolvePersonalPin(pinned.slice(2))
      if (personalPin != null) return personalPin.text
      // The pinned line vanished — deleted, or its TTL lapsed — since it was
      // pinned. Fall through to a fresh draw exactly as if there had been no
      // pin at all, rather than throwing or showing a blank toast.
    }
  } else if (pinned != null && pinned >= 0 && pinned < resolved.pool.length) {
    return resolved.pool[pinned]!(full)
  }

  const memory = readMemory(owner)
  const history = pruneHistory(memory[resolved.key] ?? [], now)
  const index = pickIndex(resolved.pool.length, history, now, rng)
  if (index < 0) return null

  writeMemory(owner, {
    ...memory,
    [resolved.key]: [...history.filter((pick) => pick.i !== index), { i: index, t: now }],
  })

  const personal = personalOverride(event, full, rng)
  const pin: number | string = personal != null ? `p:${personal.id}` : index
  // Yesterday's pins are dead weight; a write is the natural moment to shed them.
  const kept: DailyPicks = { [dayKey]: pin }
  for (const [key, value] of Object.entries(daily)) {
    if (key.endsWith(`#${dateKey}`)) kept[key] = value
  }
  kept[dayKey] = pin
  writeDaily(owner, kept)
  return personal != null ? personal.text : resolved.pool[index]!(full)
}
